/**
 * Created by piotr.pozniak@thebeaverhead.com on 25/02/2019.
 */

import React, { useCallback, useMemo } from "react";
import { useCurrentOrganizationStore } from "../../../../hooks/redux/currentOrganization";
import { useSubscriptionPlansStore } from "../../../../hooks/redux/subscriptionPlans";
import { Link } from "react-router-dom";
import OrganizationSubscriptionLink from "./OrganizationSubscriptionLink";
import { PLANS } from "../../../../consts";
import classNames from "classnames";
import ExpandableAlert from "../../component/ExpandableAlert";

const dateFormat = Intl.DateTimeFormat(window.navigator.language || "en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
});

/**
 *
 * @param {function} onUpdatePlan
 * @param {function} onShowBillingHistory
 * @param {function} onCancelSubscription
 * @param {function} onChangeBillingDetails
 * @param {function} onAddPaymentMethod
 * @param {boolean} disabled
 * @returns {Element}
 * @constructor
 */
const OrganizationSubscription = ({
  onUpdatePlan,
  onShowBillingHistory,
  onCancelSubscription,
  onChangeBillingDetails = () => {},
  disabled = false,
  onAddPaymentMethod,
}) => {
  /**
   *
   * @param callback
   * @returns {function(*): boolean}
   */
  const onClickCallback = useCallback(
    (callback) => (e) => {
      e.preventDefault();
      if (disabled) {
        return;
      }

      callback();
      return false;
    },
    [disabled]
  );

  const { currentOrganization } = useCurrentOrganizationStore();
  const currentSubscription = currentOrganization.subscription;

  // console.log(currentSubscription);

  const { subscriptionPlans } = useSubscriptionPlansStore();
  const subscriptionPlan = useMemo(
    () =>
      subscriptionPlans.collection.find(
        (i) => i.uuid == currentSubscription.uuid
      ),
    [subscriptionPlans.collection, currentSubscription]
  );

  const organizationModel = currentOrganization.model;

  const isAnnual = currentSubscription.is_annual * 1 ? true : false;
  const isDue =
    currentSubscription.available &&
    currentSubscription.expiry < Number.parseInt(new Date() / 1000);

  const isPaymentFailed =
    organizationModel.organization_settings
      .sent_timestamp_renewal_failed_period_1;

  const isTrial =
    currentSubscription && currentSubscription.is_trial * 1 ? true : false;

  const subscriptionBadge = useMemo(() => {
    let label = "Transitioning";
    let badgeClass = "badge-secondary";

    if (currentSubscription.is_trial) {
      label = "Trial";
      badgeClass = "badge-info";
    } else if (currentSubscription.is_terminated * 1) {
      label = "Cancelled";
      badgeClass = "badge-warning";
    } else if (isDue) {
      label = "Due";
      badgeClass = "badge-danger";
    } else {
      label = "Active";
      badgeClass = "badge-success";
    }
    return <span className={`badge ${badgeClass}`}>{label}</span>;
  }, [currentSubscription, isDue]);

  const validUntil = currentSubscription.expiry ? (
    <span>
      <strong>
        {isDue ? "has expired" : "is valid until"}{" "}
        {dateFormat.format(new Date(currentSubscription.expiry * 1000))}
      </strong>
      .
    </span>
  ) : null;

  const paymentDue = isDue ? (
    <div className={"alert alert-danger"}>
      Your subscription has expired. Please update your billing details to
      continue using the service.
    </div>
  ) : null;

  const paymentFailed = isPaymentFailed ? (
    <div className={"alert alert-danger"}>
      Payment failed. Your account will revert to the FREE PLAN within 3 days if
      you decide to do nothing.
    </div>
  ) : null;

  let nextPayment =
    !currentSubscription.is_trial &&
    !currentSubscription.is_terminated &&
    currentSubscription.expiry &&
    !isDue ? (
      <p>
        The plan will auto-renew on{" "}
        <strong>
          {dateFormat.format(new Date(currentSubscription.expiry * 1000))}{" "}
        </strong>
      </p>
    ) : null;

  const billingEmail = (
    <p>
      Billing email: <strong>{organizationModel.treasurer_email}</strong>{" "}
      <span className={"d-inline-flex"}>
        <button
          type="button"
          className={"btn btn-link p-0 align-baseline text-primary"}
          onClick={onClickCallback(onChangeBillingDetails)}
          disabled={disabled}
          title="Opens Stripe billing portal"
        >
          edit <i className={"material-icons icon-xs"}>open_in_new</i>
        </button>
      </span>
    </p>
  );

  const planTerminated =
    currentSubscription.is_terminated * 1 ? (
      <div className={"alert alert-warning"}>
        You have canceled your subscription. You can renew it by clicking the
        button below.
      </div>
    ) : null;

  const submitButtonLabel =
    currentSubscription.is_terminated * 1 ? "Renew plan" : "Change plan";

  const callToActionButton = (
    <button
      className={classNames("btn", {
        "btn-primary": !Boolean(currentSubscription.is_trial),
        "btn-outline-primary": Boolean(currentSubscription.is_trial),
      })}
      onClick={onUpdatePlan}
      disabled={disabled || currentSubscription.is_bundle}
    >
      {submitButtonLabel}
    </button>
  );

  const bundleInfo = currentSubscription.is_bundle ? (
    <ExpandableAlert
      header={"You’re currently on the Fluid Ministry Bundle."}
      errorLevel={"info"}
      wrapperClassName={"m-t-5"}
      body={
        <div>
          <p>
            To change your Display.Church plan, you’ll need to{" "}
            <a href={"#"} onClick={onClickCallback(onCancelSubscription)}>
              cancel this bundle
            </a>{" "}
            first where it is managed.
          </p>
          <p>
            <strong>Important</strong>: There are no prorated refunds for
            Display.Church when canceling a bundle. We recommend canceling
            closer to your renewal date.
          </p>
          <p>
            After you cancel the bundle, you will continue to have access to
            Display.Church until the current billing period ends.
          </p>
          <p>
            When your bundle fully expires, you’ll be able to choose a new plan
            directly inside Display.Church.
          </p>
        </div>
      }
    />
  ) : null;

  const addPaymentMethod = Boolean(currentSubscription.is_trial) ? (
    <button
      className="btn btn-primary"
      onClick={onClickCallback(onChangeBillingDetails)}
      disabled={disabled}
    >
      Add payment method
    </button>
  ) : null;

  const subscriptionLinks = [];
  if (!isTrial) {
    subscriptionLinks.push(
      <OrganizationSubscriptionLink
        label={"Update billing details"}
        onClick={onClickCallback(onChangeBillingDetails)}
        disabled={disabled}
      />
    );
  }

  if (
    !currentSubscription.is_trial &&
    !(currentSubscription.is_terminated * 1)
  ) {
    subscriptionLinks.push(
      <OrganizationSubscriptionLink
        label={"Cancel subscription"}
        onClick={onClickCallback(onCancelSubscription)}
        disabled={disabled}
      />
    );
  }

  if (isTrial && !Boolean(currentSubscription.is_terminated)) {
    subscriptionLinks.push(
      <OrganizationSubscriptionLink
        label={"Cancel Trial"}
        onClick={onCancelSubscription}
      />
    );
  }
  if (!isTrial) {
    subscriptionLinks.push(
      <OrganizationSubscriptionLink
        label={"Show payments history"}
        onClick={onClickCallback(onShowBillingHistory)}
        disabled={disabled}
      />
    );
  }

  const subscriptionLinksAnchors = subscriptionLinks.flatMap((item, index) =>
    index < subscriptionLinks.length - 1
      ? [
          item,
          <span key={`sep-${index}`} className={"text-muted-light"}>
            {" "}
            &bull;{" "}
          </span>,
        ]
      : [item]
  );

  if (currentSubscription.code === PLANS.free) {
    return null;
  }

  const planDetails =
    currentSubscription && currentSubscription.expiry ? (
      <div className="card-box">
        <h4 className={"card-title d-flex gap-xs align-items-start"}>
          <span>
            {currentSubscription.name} - {isAnnual ? "Yearly" : "Monthly"}{" "}
            renewal
          </span>
          {subscriptionBadge}{" "}
        </h4>
        <p className={""}>Subscription {validUntil}</p>
        {/*{creditBalance}*/}
        {planTerminated}
        {nextPayment}
        {paymentDue}
        {paymentFailed}
        {billingEmail}
        <div className={"d-flex gap-xs"}>
          {callToActionButton}
          {addPaymentMethod}
        </div>
        {bundleInfo}
        <p>
          <br />
          {subscriptionLinksAnchors}
        </p>
      </div>
    ) : (
      <div className="card-box">
        {/*{creditBalance}*/}
        {planTerminated}
        {nextPayment}
        {paymentDue}

        <div className={"d-flex gap-xs"}>
          {callToActionButton}
          {addPaymentMethod}
        </div>
        {bundleInfo}

        <p>
          <br />
          {subscriptionLinksAnchors}
        </p>
      </div>
    );

  return (
    <div className="row">
      <div className="col-12">{planDetails}</div>
    </div>
  );
};

export default OrganizationSubscription;
